import type { ValueOf } from 'type-fest';
import type { Identifier } from '../../common/identifier';
import type { Event } from '../event';
import { _Config } from './config';

const Code = {
  L0: 'L0',
  L1: 'L1',
  L2: 'L2',
  L3: 'L3',
  L4: 'L4',
} as const;

export type IdentityLevel = ValueOf<typeof Code>;
export namespace IdentityLevel {
  export import Config = _Config;

  // L3/L4かどうかを判定（verified cluster形成に使用可能か）
  export const isVerifiedLevel = (level: IdentityLevel): boolean => {
    return level === Code.L3 || level === Code.L4;
  };

  // L1/L2かどうかを判定（behavior cluster形成に使用可能か）
  // NOTE: Evidence が排他的であり、IdentityNode は排他的ではない。
  // 同じ Node が異なるレベルの Evidence を持つ場合、両方のクラスタに所属しうる。
  export const isBehaviorLevel = (level: IdentityLevel): boolean => {
    return level === Code.L1 || level === Code.L2;
  };

  // レベルの比較（数値として比較）
  export const compare = (a: IdentityLevel, b: IdentityLevel): number => {
    const order = { L0: 0, L1: 1, L2: 2, L3: 3, L4: 4 };
    return order[a] - order[b];
  };

  // レベルの説明を取得
  export const getDescription = (level: IdentityLevel): string => {
    switch (level) {
      case Code.L0:
        return '未検証の自己申告';
      case Code.L1:
        return '弱いシグナル';
      case Code.L2:
        return '準一意';
      case Code.L3:
        return '認証済みアカウント';
      case Code.L4:
        return '強い本人確認済み';
    }
  };

  export const FromString = (s: string): IdentityLevel => {
    const value = Code[s as keyof typeof Code] as IdentityLevel;
    if (!value) throw new Error(`Invalid IdentityLevel: ${s}`);
    return value;
  };

  /**
   * identifierType × eventType に対するデフォルト assuranceLevel を解決する。
   * 同一 identifier について、イベント完全一致をワイルドカード（*）より優先する。
   */
  export const resolveDefault = (identifierType: Identifier.Type, eventType: Event.Type): IdentityLevel => {
    const defaults = Config.Default;
    const forIdentifier = defaults.filter((row) => row.identifierType === identifierType);
    const eventStr = eventType as string;
    const exact = forIdentifier.find((row) => {
      const rowEt = row.eventType as string;
      return rowEt !== '*' && rowEt === eventStr;
    });
    if (exact) return exact.defaultLevel;
    const wild = forIdentifier.find((row) => (row.eventType as string) === '*');
    if (wild) return wild.defaultLevel;
    return Config.FALLBACK_LEVEL;
  };
}
